/**
 * DM Screen Parent Window Helper Controller
 *
 * Stimulus controller to coordinate Storage Access API requests with embedded iframes.
 *
 * Usage:
 * <div data-controller="dm-screen-helper">
 *   <iframe data-dm-screen-helper-target="iframe" data-original-src="https://example.com" src="about:blank"></iframe>
 * </div>
 */

import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['iframe'];
  static values = {
    autoSetup: { type: Boolean, default: true },
    loadDelay: { type: Number, default: 500 },
    addOnGuideUrl: String
  };

  connect() {
    console.log('[DM Screen Helper] Controller connected');

    // Listen for messages from iframes
    this.handleMessage = this.handleMessage.bind(this);
    window.addEventListener('message', this.handleMessage);

    // Initialize add-on detection flag
    this.addOnMessageReceived = false;

    // Check for Firefox and add-on
    this.checkFirefoxAddOn();

    // Setup existing iframes
    if (this.autoSetupValue) {
      this.setupIframes();
    }

    // Setup mutation observer for dynamically added iframes
    this.setupMutationObserver();
  }

  disconnect() {
    console.log('[DM Screen Helper] Controller disconnected');
    window.removeEventListener('message', this.handleMessage);

    if (this.observer) {
      this.observer.disconnect();
    }
  }

  /**
   * Called when iframe targets are connected
   */
  iframeTargetConnected(iframe) {
    console.log('[DM Screen Helper] Iframe target connected:', iframe.src);
    this.setupIframe(iframe);
  }

  /**
   * Setup a single iframe with load listener
   */
  setupIframe(iframe) {
    if (!iframe || !iframe.contentWindow) {
      console.error('[DM Screen Helper] Invalid iframe element');
      return;
    }

    // Add load listener if not already added
    if (!iframe.dataset.dmScreenHelperAttached) {
      iframe.dataset.dmScreenHelperAttached = 'true';

      iframe.addEventListener('load', () => {
        console.log('[DM Screen Helper] Iframe loaded:', iframe.src);

        // Give the iframe content script time to initialize
        setTimeout(() => {
          this.requestIframeStorageAccess(iframe);
        }, this.loadDelayValue);
      });
    }
  }

  /**
   * Setup all iframe targets
   */
  setupIframes() {
    this.iframeTargets.forEach(iframe => {
      this.setupIframe(iframe);
    });
  }

  /**
   * Send a message to an iframe to request storage access
   */
  requestIframeStorageAccess(iframe) {
    if (!iframe || !iframe.contentWindow) {
      console.error('[DM Screen Helper] Invalid iframe element');
      return;
    }

    try {
      iframe.contentWindow.postMessage({
        type: 'dm-screen-request-storage-access'
      }, '*');
      console.log('[DM Screen Helper] Sent storage access request to iframe');
    } catch (error) {
      console.error('[DM Screen Helper] Failed to send message to iframe:', error);
    }
  }

  /**
   * Handle messages from iframes
   */
  handleMessage(event) {
    const { data } = event;

    if (!data || !data.type) {
      return;
    }

    // Handle storage access granted
    if (data.type === 'dm-screen-storage-access-granted') {
      this.addOnMessageReceived = true;
      console.log('[DM Screen Helper] Storage access granted in iframe:', event);

      // Find the iframe by matching its contentWindow to event.source
      const iframe = this.iframeTargets.find(
        frame => frame.contentWindow === event.source
      );

      // Also check non-target iframes within the controller element
      const allIframes = iframe ? [iframe] : Array.from(this.element.querySelectorAll('iframe')).filter(
        frame => frame.contentWindow === event.source
      );

      const matchedIframe = allIframes[0];

      console.log('[DM Screen Helper] Found iframe to redirect', matchedIframe, matchedIframe?.dataset.originalSrc);

      if (matchedIframe && matchedIframe.dataset.originalSrc) {
        console.log(`[DM Screen Helper] Replying with iframe's original SRC: ${matchedIframe.dataset.originalSrc}`);
        event.source.postMessage({
          type: 'dm-screen-storage-access-response',
          id: data.id,
          redirect_url: matchedIframe.dataset.originalSrc
        }, event.origin);
      }
    }
    // Handle storage access denied
    else if (data.type === 'dm-screen-storage-access-denied') {
      this.addOnMessageReceived = true;
      console.warn('[DM Screen Helper] Storage access denied in iframe:', event);
    }
  }

  /**
   * Setup mutation observer to detect dynamically added iframes
   */
  setupMutationObserver() {
    this.observer = new MutationObserver((mutations) => {
      let hasNewIframes = false;

      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.tagName === 'IFRAME') {
            hasNewIframes = true;
          } else if (node.querySelectorAll) {
            const iframes = node.querySelectorAll('iframe');
            if (iframes.length > 0) {
              hasNewIframes = true;
            }
          }
        });
      });

      if (hasNewIframes) {
        console.log('[DM Screen Helper] New iframes detected, setting up helpers');
        this.setupIframes();
      }
    });

    this.observer.observe(this.element, {
      childList: true,
      subtree: true
    });

    console.log('[DM Screen Helper] Monitoring for new iframes');
  }

  /**
   * Action: Manually trigger storage access request for all iframes
   */
  requestStorageAccess() {
    console.log('[DM Screen Helper] Manually requesting storage access for all iframes');
    this.iframeTargets.forEach(iframe => {
      this.requestIframeStorageAccess(iframe);
    });
  }

  /**
   * Check if browser is Firefox and if the DM Screen add-on is installed
   */
  checkFirefoxAddOn() {
    // Check if already shown
    const hasSeenModal = localStorage.getItem('dm-screen-addon-modal-shown');
    if (hasSeenModal === 'true') {
      console.log('[DM Screen Helper] Add-on modal already shown previously');
      return;
    }

    // Check if Firefox
    if (!this.isFirefox()) {
      console.log('[DM Screen Helper] Not Firefox browser, skipping add-on check');
      return;
    }

    console.log('[DM Screen Helper] Firefox detected, checking for add-on...');

    setTimeout(() => {
      if (!this.addOnMessageReceived) {
        console.log('[DM Screen Helper] Add-on not detected, showing recommendation modal');
        this.showAddOnRecommendationModal();
      } else {
        console.log('[DM Screen Helper] Add-on detected, no modal needed');
      }
    }, 5000);
  }

  /**
   * Detect if the browser is Firefox
   */
  isFirefox() {
    return navigator.userAgent.toLowerCase().includes('firefox');
  }

  /**
   * Show the Firefox add-on recommendation in the shared Turbo modal frame.
   */
  showAddOnRecommendationModal() {
    const modalFrame = document.getElementById('modal');
    if (!modalFrame || !this.hasAddOnGuideUrlValue) return;

    localStorage.setItem('dm-screen-addon-modal-shown', 'true');
    modalFrame.src = this.addOnGuideUrlValue;
    console.log('[DM Screen Helper] Add-on recommendation modal displayed');
  }
}
